import React, { FunctionComponent, ReactNode, useRef } from 'react'
import { PaymentScheduleTitle } from '../PaymentScheduleTitle'
import { ModuleContainer } from '../ModuleContainer'
import { PaymentSchedule } from '../PaymentSchedule'
import { Divider, Override, DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { useCheckoutState, useExperiment, useIsBillingAgreementApprovalProcess, useSummaryImprovementsExperiment } from '../../state'
import { PaymentMethod } from '../PaymentMethod'
import { CheckoutStatus, ExperimentValue, Features, PageLayout } from '../../model'
import styles from './style.module.css'
import clsx from 'clsx'
import { AddPaymentMethodSummary } from '../AddPaymentMethodSummary'
import { useFlag } from '@afterpaytouch/feature'
import { PaymentScheduleExperiment } from '../PaymentScheduleExperiment'
import { PaymentDay } from '../PaymentDay'
import { AutopayButton } from '../AutopayToggle/AutopayButton'
import { PreferredCardTopazError } from '../PreferredCardTopazError'

interface PaymentScheduleContainerProps {
  noBottomMargin?: boolean
  isSummary?: boolean
  testNameSpace?: string
}

export const PaymentScheduleContainer: FunctionComponent<PaymentScheduleContainerProps> = ({
  isSummary = false,
  noBottomMargin = false,
  testNameSpace = '',
}) => {
  const paymentScheduleRef = useRef<null | HTMLDivElement>(null)

  const { cashTooltipShown, status } = useCheckoutState()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const { control: isPaymentSchedule } = useExperiment('CheckoutShowPaymentScheduleOnAddPaymentMethod', !isSummary)
  const addFirstPaymentMethodExperimentVariant = useFlag<ExperimentValue>(Features.CHECKOUT_ADD_FIRST_PAYMENT_METHOD, 'E')
  const showPaymentSchedule = (!isPaymentSchedule && !isBillingAgreementApprovalProcess) || addFirstPaymentMethodExperimentVariant === 'A'
  const classNames = clsx(!isSummary && showPaymentSchedule && styles.paymentSchedule)
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)

  // add first payment method experiment
  const isCheckoutStatusFirstPaymentMethodRequired = status === CheckoutStatus.FirstPaymentMethodRequired
  const isAddFirstPaymentMethodExperimentBEligible = addFirstPaymentMethodExperimentVariant === 'B' && isCheckoutStatusFirstPaymentMethodRequired

  const { enabled: summaryImprovementExpEnabled, value: summaryImprovementExpVal } = useSummaryImprovementsExperiment()

  if (!isSummary && !showPaymentSchedule) {
    return null
  }

  const renderPaymentMethod = (): ReactNode => {
    if (isSummary) {
      if (isAddFirstPaymentMethodExperimentBEligible) {
        return <AddPaymentMethodSummary />
      } else {
        return (
          <PaymentMethod
            negativeMargins={isBronteDesignLanguage}
            layout={PageLayout.Summary}
            optionRadiusOverride={Override.DisabledTopBorderRadius}
            showErrorMessage={false}
          />
        )
      }
    }
    return null
  }

  const renderPaymentScheduleAndPaymentMethod = (): ReactNode => {
    const showPaymentScheduleExperiment =
      isSummary && summaryImprovementExpEnabled && (summaryImprovementExpVal === 'A' || summaryImprovementExpVal === 'E') && isBronteDesignLanguage

    return showPaymentScheduleExperiment ? (
      <PaymentScheduleExperiment />
    ) : (
      <>
        <PaymentScheduleTitle />
        <ModuleContainer noBottomMargin={isBronteDesignLanguage && noBottomMargin} extraBottomMargin={cashTooltipShown} nested={!isBronteDesignLanguage}>
          <PaymentSchedule />
          {isSummary && isBronteDesignLanguage && <Divider kind='Hairline' />}
          <PaymentDay />
          <AutopayButton />
          {renderPaymentMethod()}
        </ModuleContainer>
      </>
    )
  }

  return (
    <>
      <div ref={paymentScheduleRef} className={classNames} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-payment-schedule-container` })}>
        {!isBillingAgreementApprovalProcess ? (
          renderPaymentScheduleAndPaymentMethod()
        ) : (
          <ModuleContainer showBorder={isBronteDesignLanguage} extraBottomMargin={cashTooltipShown}>
            {isSummary && <PaymentMethod layout={PageLayout.Summary} />}
          </ModuleContainer>
        )}
      </div>
      {isSummary && <PreferredCardTopazError />}
    </>
  )
}
